import { Tabs } from '@/components/tabs/Tabs';
import { useSlabs } from '@/features/configuration-management/commision-management/hooks/useSlab';
import { SlabsTable } from '@/features/configuration-management/commision-management/slab/table';
import { encryptedStatusTypeMap } from '@/features/configuration-management/hoslog-plans/utils/hoslogPlan.constant';
import { useDynamicLocale } from '@/hoslog-intl';
import { createFileRoute, SearchSchemaInput } from '@tanstack/react-router';
import { parseAsString, useQueryState } from 'nuqs';
import { z } from 'zod';

enum tabValue {
  ADMIN = 'Admin',
  PLAN = 'Plan',
}
const sortItemSchema = z.object({
  id: z.enum([
    'fromAmount',
    'toAmount',
    'commissionPercentageType',
    'commissionValue',
    'minCommissionValue',
    'maxCommissionValue',
  ]),
  desc: z.boolean(),
});

const SlabSearchSchema = z
  .object({
    sort: z
      .preprocess((val) => {
        if (typeof val === 'string') {
          try {
            return JSON.parse(val);
          } catch {
            return undefined;
          }
        }
        return val;
      }, z.array(sortItemSchema))
      .optional(),
    createdDate: z.string().optional(),

    page: z.coerce.number().positive().catch(0),
    fetch: z.coerce.number().positive().max(100).catch(10),
    activeTab: z.enum([tabValue.ADMIN, tabValue.PLAN]).default(tabValue.ADMIN),
  })
  .passthrough()
  .transform((data) => {
    return {
      ...data,
      adminCommissionTypeId:
        data.activeTab === tabValue.PLAN ? encryptedStatusTypeMap.request : encryptedStatusTypeMap.list,
    };
  });

export const Route = createFileRoute('/(app)/configuration-management/commissions/$categoryId/')({
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => SlabSearchSchema.parse(search),
});

function RouteComponent() {
  const { slab, isLoading, meta } = useSlabs();
  const { categoryName } = Route.useSearch();
  const { t } = useDynamicLocale();
  const tabItems = [
    {
      label: t('configurationManagement.commissions.table.title.admin'),
      value: tabValue.ADMIN,
      content: <SlabsTable data={slab} isLoading={isLoading} pagination={meta} categoryName={categoryName} />,
    },
    {
      label: t('configurationManagement.commissions.table.title.plan'),
      value: tabValue.PLAN,
      content: <SlabsTable data={slab} isLoading={isLoading} pagination={meta} categoryName={categoryName} />,
    },
  ];

  const [activeTabProp, setActiveTabProp] = useQueryState('activeTab', parseAsString.withDefault(tabValue.ADMIN));
  const onTabChange = (tab: string): void => {
    void setActiveTabProp(tab);
  };

  return (
    <Tabs
      className="rounded-lg shadow bg-muted"
      listClassName="bg-muted"
      triggerClassName="bg-muted rounded-t-[8px] px-6 py-3"
      defaultActive={activeTabProp}
      items={tabItems}
      onChange={onTabChange}
    />
  );
}
